<template>
  <a
    ref="reference"
    class="tw-flex tw-items-center tw-cursor-pointer tw-no-underline tw-text-gray-800 company-switcher hover:tw-bg-blue-400"
    style="padding: 0.85rem 1.3rem 0.85rem 1.2rem; border-radius: 6px; margin: 0 -0.75rem 0 0"
  >
    <span
      :title="activeCompany.name"
      class=" tw-font-normal tw-truncate"
      style="max-width: 270px; font-size: 1.23rem; margin: -1px -3px 0 0;"
      data-cy="company-switcher"
      v-text="activeCompany.name"
    />
    <SvgIcon name="cheveron-down" class="tw-text-gray-800 cheveron-down" />

    <div v-show="false" ref="tooltip">
      <div
        data-cy="company-switcher-list"
        class="tw-border tw-border-opacity-100"
        style="border-radius: 0.375rem"
      >
        <div class="tw-border-b tw-border-gray-350">
          <div
            class="tw-flex tw-items-center tw-justify-start tw-relative"
            style="padding: 0.5rem 0 0.5rem 1.1rem;"
          >
            <span
              class=" tw-text-gray-800 tw-font-medium tw-text-xl tw-cursor-default"
              >Companies</span
            >
          </div>
        </div>
        <div class="tw-overflow-auto" style="max-height: 500px;">
          <div
            v-for="company in sortedCompanies"
            :key="company.id"
            data-cy="company-switcher-company"
          >
            <a
              class="tw-flex tw-items-center tw-justify-between tw-no-underline tw-cursor-pointer tw-overflow-hidden hover:tw-bg-yellow-200 tw-group"
              style="padding: 0.25rem 1.25rem 0.3rem 1.25rem;"
              @click="changeActiveCompany(company)"
            >
              <div
                class="tw-font-normal tw-truncate"
                style="font-size: 1.23rem;max-width: 300px"
              >
                {{ company.name }}
              </div>
              <SpinnerButton
                :id="company.id"
                name="default-company"
                :disabled="isDefaultCompany(company)"
                data-cy="default-company-selector"
                type="button"
                theme="white"
                :loading="isCompanySaving"
                :class="getButtonVisibilityClasses(company)"
                style="margin-left:1rem; padding: 0.35rem 0.6rem 0.36rem 0.68rem;width: 170px"
                :spinner-classes="['tw-text-black']"
                spinner-text-classes="tw-text-xl"
                @click="changeDefaultCompany(company.id)"
              >
                <div class="tw-flex tw-items-center tw-content-center">
                  <SvgIcon name="star" class="tw-w-5 tw-h-5" />
                  <div class="tw-font-medium tw-text-xl tw-mx-2">
                    {{ getButtonLabel(company) }}
                  </div>
                </div>
              </SpinnerButton>
            </a>
          </div>
        </div>
        <div
          v-if="isActiveEmploymentAnAdmin"
          class="tw-border-t tw-text-xl tw-border-gray-350 hover:tw-bg-yellow-200"
          data-cy="manage-companies"
        >
          <button
            class="tw-cursor-pointer tw-text-blue-600 tw-font-medium tw-overflow-hidden tw-no-underline tw-w-full tw-text-left"
            style="padding:0.66rem 1rem 0.63rem 1.1rem; "
            type="button"
            title="Manage Companies [alt+c]"
            data-cy="btn-manage-companies"
            @click="manageCompanies"
          >
            Manage companies
          </button>
        </div>
      </div>
    </div>
  </a>
</template>

<script>
import tippy from 'tippy.js'
import { orderBy } from 'lodash-es'
import { mapGetters, mapMutations } from 'vuex'
import SpinnerButton from '@/components/SpinnerButton'

export default {
  name: 'CompanySwitcher',
  components: { SpinnerButton },

  props: {
    isCompanySaving: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('auth', ['companies']),

    sortedCompanies() {
      return orderBy(
        this.companies,
        [company => company.name.toLowerCase()],
        'asc'
      )
    },

    defaultCompanyId: {
      get() {
        return this.authUser.settings.default_company
      },
      set(defaultCompanyId) {
        this.setAuthUser({
          ...this.authUser,
          settings: {
            ...this.authUser.settings,
            default_company: defaultCompanyId,
          },
        })
      },
    },
  },

  mounted() {
    this.tippy = tippy.one(this.$refs.reference, {
      html: this.$refs.tooltip.firstChild,
      trigger: 'click',
      distance: -17.5,
      placement: 'bottom-end',
      offset: '1,19',
      interactive: true,
      theme: 'light',
      animation: 'scale',
      inertia: true,
      arrow: true,
    })

    this.$once('hook:beforeDestroy', () => {
      this.tippy.destroy()
    })
  },

  methods: {
    ...mapMutations('auth', ['setAuthUser']),

    getButtonLabel(company) {
      return this.isDefaultCompany(company) ? 'Default' : 'Set as default'
    },

    getButtonVisibilityClasses(company) {
      return this.isDefaultCompany(company)
        ? ''
        : 'tw-invisible group-hover:tw-visible'
    },

    isDefaultCompany(company) {
      return this.defaultCompanyId === company.id
    },

    async changeActiveCompany(company) {
      this.$router.push(
        {
          query: {
            company: company.id,
          },
        },
        () => {}
      )

      this.tippy.hide()
    },

    manageCompanies() {
      window.location.href = `/companies?company=${this.activeCompany.id}`
    },

    changeDefaultCompany(companyId) {
      this.defaultCompanyId = companyId

      this.$emit('default-company-changed', this.defaultCompanyId)
    },
  },
}
</script>

<style scoped>
.company-switcher {
  padding: 8px 7px;
}

.cheveron-down {
  height: 1.75rem;
  width: 2.2rem;
  margin: -0.17rem -1rem 0 0;
}

.tippy-active {
  @apply tw-bg-blue-300 !important;
}
</style>
